import { ReactNode } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

export type SelectableCardOption<K extends string> = {
  key: K;
  label: string;
};

export type SelectableCardColumns = 1 | 2 | 3 | 4 | 5;

const COLUMN_BREAKPOINTS: Record<
  SelectableCardColumns,
  { sm: number; md: number }
> = {
  1: { sm: 12, md: 12 },
  2: { sm: 12, md: 6 },
  3: { sm: 6, md: 4 },
  4: { sm: 6, md: 3 },
  5: { sm: 5, md: 2.3 },
};

type Props<K extends string> = {
  options: ReadonlyArray<SelectableCardOption<K>>;
  isSelected: (key: K) => boolean;
  onToggle: (key: K) => void;
  /** Render extra content inside the selected card. Return null/undefined for cards without expanded content. */
  renderExpanded?: (option: SelectableCardOption<K>) => ReactNode;
  /** Desktop column count. Mobile always stacks to 1; tablet uses sensible defaults. */
  columns?: SelectableCardColumns;
  buttonHeight?: number | string;
  /** When set, columns are exactly this wide (CSS grid fixed tracks). */
  buttonWidth?: number | string;
};

const toCssSize = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

/**
 * Grid of selectable cards. Pure presentational — the caller owns the
 * selection state and toggle handler (typically backed by react-hook-form).
 * Optionally renders custom content inside the selected card via the
 * `renderExpanded` prop (e.g. a description input for an "Other" card).
 */
const SelectableCardGroup = <K extends string>({
  options,
  isSelected,
  onToggle,
  renderExpanded,
  columns = 3,
  buttonHeight,
  buttonWidth,
}: Props<K>) => {
  const { sm, md } = COLUMN_BREAKPOINTS[columns];
  const useFixedGrid = buttonWidth != null;
  const trackWidth = buttonWidth != null ? toCssSize(buttonWidth) : undefined;

  const renderCard = (option: SelectableCardOption<K>) => {
    const checked = isSelected(option.key);
    const expanded = checked ? renderExpanded?.(option) : null;
    return (
      <Paper
        key={option.key}
        variant="outlined"
        data-testid={option.key}
        onClick={() => onToggle(option.key)}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          borderRadius: '8px',
          boxSizing: 'border-box',
          width: useFixedGrid ? '100%' : undefined,
          ...(buttonHeight != null && {
            minHeight: buttonHeight,
            ...(expanded == null && { height: buttonHeight }),
          }),
          ...(!useFixedGrid &&
            buttonWidth != null && {
              width: '100%',
              maxWidth: buttonWidth,
            }),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          bgcolor: checked ? 'rgba(59, 84, 130, 0.06)' : 'background.paper',
          border: (theme) =>
            checked
              ? `1px solid ${theme.palette.secondary.main}`
              : `1px solid ${theme.palette.divider}`,
          transition: 'background-color 0.15s, border-color 0.15s',
          '&:hover': {
            borderColor: (theme) => theme.palette.secondary.main,
            bgcolor: checked ? 'rgba(59, 84, 130, 0.08)' : 'grey.50',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ...(buttonHeight != null && expanded == null && { flex: 1 }),
          }}
        >
          {checked ? (
            <CheckBoxIcon sx={{ fontSize: 22, color: 'secondary.main' }} />
          ) : (
            <CheckBoxOutlineBlankIcon
              sx={{ fontSize: 22, color: 'action.disabled' }}
            />
          )}
          <Typography
            variant="body1"
            sx={{
              fontWeight: checked ? 600 : 400,
              color: 'text.primary',
            }}
          >
            {option.label}
          </Typography>
        </Box>
        {expanded && (
          <Box
            sx={{
              mt: 1.25,
              '& .MuiInputBase-root': { bgcolor: 'background.paper' },
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {expanded}
          </Box>
        )}
      </Paper>
    );
  };
  if (useFixedGrid) {
    return (
      <Box
        sx={{
          display: 'grid',
          width: { xs: '100%', sm: '100%', md: 'fit-content' },
          maxWidth: '100%',
          gap: 2,
          // Mobile: 1 column = full button width; md+: N columns × buttonWidth
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: `repeat(auto-fill, ${trackWidth})`,
            md: `repeat(${columns}, ${trackWidth})`,
          },
        }}
      >
        {options.map(renderCard)}
      </Box>
    );
  }
  return (
    <Grid container spacing={2}>
      {options.map((option) => (
        <Grid item xs={12} sm={sm} md={md} key={option.key}>
          {renderCard(option)}
        </Grid>
      ))}
    </Grid>
  );
};
export default SelectableCardGroup;
